import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { optionalIntegerString, parseIntegerString } from "../units";
import { fetchJson } from "./client";
import { currentCache, immutableCache } from "./cacheClasses";
import { queryKeys } from "./queryKeys";
import { isValidBlockHeight } from "./validation";

// explorer-api serializes with JsonIgnoreCondition.WhenWritingNull, so nullable
// fields are absent (not `null`) on the wire. Model them as optional here
// so the hydrators can safely treat undefined as "no value".

interface WireRegistrationsEntry {
  readonly height: string;
  readonly blockTime?: string;
  readonly newRegistrations: number;
  readonly cumulativeTotal: string;
}

interface WireRegistrationsResponse {
  readonly fromHeight?: string;
  readonly toHeight: string;
  readonly entries: readonly WireRegistrationsEntry[];
}

interface WireActiveMiningResponse {
  readonly asOfHeight: string;
  readonly windowBlocks: number;
  readonly fromHeight: string;
  readonly activeCount: number;
}

interface WireLeaderboardEntry {
  readonly domainId: number;
  readonly name?: string;
  readonly wins: number;
  readonly cogtoshiWon: string;
}

interface WireLeaderboardResponse {
  readonly fromHeight?: string;
  readonly toHeight: string;
  readonly limit: number;
  readonly entries: readonly WireLeaderboardEntry[];
}

export interface RegistrationsEntry {
  readonly height: bigint;
  readonly blockTime: bigint | null;
  readonly newRegistrations: number;
  readonly cumulativeTotal: bigint;
}

export interface DomainRegistrationsSeries {
  readonly fromHeight: bigint | null;
  readonly toHeight: bigint;
  readonly entries: readonly RegistrationsEntry[];
}

export interface ActiveMiningSnapshot {
  readonly asOfHeight: bigint;
  readonly windowBlocks: number;
  readonly fromHeight: bigint;
  readonly activeCount: number;
}

export interface LeaderboardEntry {
  readonly domainId: number;
  readonly name: string | null;
  readonly wins: number;
  readonly cogtoshiWon: bigint;
}

export interface DomainLeaderboard {
  readonly fromHeight: bigint | null;
  readonly toHeight: bigint;
  readonly limit: number;
  readonly entries: readonly LeaderboardEntry[];
}

export function hydrateRegistrationsSeries(
  wire: WireRegistrationsResponse,
): DomainRegistrationsSeries {
  return {
    fromHeight: optionalIntegerString(wire.fromHeight),
    toHeight: parseIntegerString(wire.toHeight),
    entries: wire.entries.map((e) => ({
      height: parseIntegerString(e.height),
      blockTime: optionalIntegerString(e.blockTime),
      newRegistrations: e.newRegistrations,
      cumulativeTotal: parseIntegerString(e.cumulativeTotal),
    })),
  };
}

export function hydrateActiveMining(wire: WireActiveMiningResponse): ActiveMiningSnapshot {
  return {
    asOfHeight: parseIntegerString(wire.asOfHeight),
    windowBlocks: wire.windowBlocks,
    fromHeight: parseIntegerString(wire.fromHeight),
    activeCount: wire.activeCount,
  };
}

export function hydrateLeaderboard(wire: WireLeaderboardResponse): DomainLeaderboard {
  return {
    fromHeight: optionalIntegerString(wire.fromHeight),
    toHeight: parseIntegerString(wire.toHeight),
    limit: wire.limit,
    entries: wire.entries.map((e) => ({
      domainId: e.domainId,
      name: e.name ?? null,
      wins: e.wins,
      cogtoshiWon: parseIntegerString(e.cogtoshiWon),
    })),
  };
}

function withFromHeightQuery(path: string, fromHeight: number | null): string {
  return fromHeight === null ? path : `${path}?fromHeight=${fromHeight.toString()}`;
}

export function useDomainRegistrationsSeries(
  toHeight: number | null,
  fromHeight: number | null = null,
): UseQueryResult<DomainRegistrationsSeries> {
  return useQuery({
    queryKey: queryKeys.domains.registrationsSeries(toHeight ?? -1, fromHeight),
    queryFn: async () => {
      const path = withFromHeightQuery(
        `/api/v1/domains/registrations/series/${(toHeight ?? 0).toString()}`,
        fromHeight,
      );
      return hydrateRegistrationsSeries(await fetchJson<WireRegistrationsResponse>(path));
    },
    enabled: isValidBlockHeight(toHeight),
    ...immutableCache,
  });
}

export function useActiveMiningCount(
  windowBlocks: number,
): UseQueryResult<ActiveMiningSnapshot> {
  return useQuery({
    queryKey: queryKeys.domains.activeMining(windowBlocks),
    queryFn: async () =>
      hydrateActiveMining(
        await fetchJson<WireActiveMiningResponse>(
          `/api/v1/domains/active-mining?windowBlocks=${windowBlocks.toString()}`,
        ),
      ),
    ...currentCache,
  });
}

export function useDomainLeaderboard(
  toHeight: number | null,
  options: { fromHeight?: number | null; limit?: number } = {},
): UseQueryResult<DomainLeaderboard> {
  const fromHeight = options.fromHeight ?? null;
  const limit = options.limit ?? 10;
  return useQuery({
    queryKey: queryKeys.domains.leaderboardByRewards(toHeight ?? -1, fromHeight, limit),
    queryFn: async () => {
      const base = `/api/v1/domains/leaderboard/by-rewards/${(toHeight ?? 0).toString()}`;
      const params = new URLSearchParams();
      if (fromHeight !== null) params.set("fromHeight", fromHeight.toString());
      params.set("limit", limit.toString());
      return hydrateLeaderboard(
        await fetchJson<WireLeaderboardResponse>(`${base}?${params.toString()}`),
      );
    },
    enabled: isValidBlockHeight(toHeight),
    ...immutableCache,
  });
}
